import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout";
import { sideBarItems } from "../../utils";
import { Pagination } from "../../components/common/pagination";
import { toast } from "react-hot-toast";
import { withRouter } from "react-router-dom";
import RideShareAdminService from "../../services/admin/RideShareAdminService";

const BID_STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Accepted", value: "accepted" },
  { label: "Rejected", value: "rejected" },
  { label: "Created", value: "created" },
  { label: "Expired", value: "expired" },
  { label: "Cancelled", value: "cancelled_by_system" },
];

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString();
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "BDT 0.00";
  return `BDT ${amount.toFixed(2)}`;
};

const getError = (err) => err?.response?.data?.error || "Operation failed";

function RideShareBids(props) {
  const { history } = props;
  const [summary, setSummary] = useState(null);
  const [driverSummary, setDriverSummary] = useState([]);

  const [driverSummaryPage, setDriverSummaryPage] = useState(1);
  const [driverSummaryLimit, setDriverSummaryLimit] = useState(15);

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  const queryParams = useMemo(() => {
    const params = {};

    if (filters.search.trim()) params.search = filters.search.trim();
    if (filters.status) params.status = filters.status;
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;

    return params;
  }, [filters]);

  const paginatedDriverSummary = useMemo(() => {
    const start = (driverSummaryPage - 1) * driverSummaryLimit;
    return driverSummary.slice(start, start + driverSummaryLimit);
  }, [driverSummary, driverSummaryPage, driverSummaryLimit]);

  const driverSummaryTotalPages =
    Math.ceil(driverSummary.length / driverSummaryLimit) || 1;

  const loadBids = useCallback(async () => {
    try {
      const res = await RideShareAdminService.getBidDetails(queryParams);
      const bidSummary = res?.data?.summary || null;
      const groupedSummary = Array.isArray(res?.data?.driverSummary)
        ? res.data.driverSummary
        : [];

      setSummary(bidSummary);
      setDriverSummary(groupedSummary);
    } catch (err) {
      toast.error(getError(err));
      setSummary(null);
      setDriverSummary([]);
    }
  }, [queryParams]);

  useEffect(() => {
    loadBids();
  }, [loadBids]);

  useEffect(() => {
    setDriverSummaryPage(1);
  }, [filters.search, filters.status, filters.fromDate, filters.toDate]);

  useEffect(() => {
    if (driverSummaryPage > driverSummaryTotalPages) {
      setDriverSummaryPage(driverSummaryTotalPages);
    }
  }, [driverSummaryPage, driverSummaryTotalPages]);

  const goToDriverBidsPage = (item) => {
    const params = new URLSearchParams();
    if (filters.fromDate) params.set("fromDate", filters.fromDate);
    if (filters.toDate) params.set("toDate", filters.toDate);
    const query = params.toString();
    history.push(
      `/ridesharebids/driver/${item?.driverId || ""}${query ? `?${query}` : ""}`,
    );
  };

  return (
    <Layout title={sideBarItems.rideShareBids.key}>
      <div className="w-full py-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-5">
          Ride Share Bid Details
        </h1>

        <div className="bg-white shadow-md rounded-lg p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input
              className="px-3 py-2 border rounded-md"
              placeholder="Search bid/ride/driver/passenger"
              value={filters.search}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, search: e.target.value }))
              }
            />
            <select
              className="px-3 py-2 border rounded-md"
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
            >
              {BID_STATUS_OPTIONS.map((item) => (
                <option key={item.value || "all"} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <input
              className="px-3 py-2 border rounded-md"
              type="date"
              value={filters.fromDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
              }
            />
            <input
              className="px-3 py-2 border rounded-md"
              type="date"
              value={filters.toDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, toDate: e.target.value }))
              }
            />
          </div>

          <div className="mt-3 flex gap-2">
            <button
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              onClick={() =>
                setFilters({
                  search: "",
                  status: "",
                  fromDate: "",
                  toDate: "",
                })
              }
            >
              Reset Filters
            </button>
            <button
              className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              onClick={loadBids}
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
          <div className="bg-white rounded-lg shadow p-3">
            <div className="text-xs text-gray-500">Total Bids</div>
            <div className="text-lg font-semibold">{summary?.total || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-3">
            <div className="text-xs text-gray-500">Accepted</div>
            <div className="text-lg font-semibold">
              {summary?.accepted || 0}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-3">
            <div className="text-xs text-gray-500">Rejected</div>
            <div className="text-lg font-semibold">
              {summary?.rejected || 0}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-3">
            <div className="text-xs text-gray-500">Created</div>
            <div className="text-lg font-semibold">{summary?.created || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-3">
            <div className="text-xs text-gray-500">Expired</div>
            <div className="text-lg font-semibold">{summary?.expired || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-3">
            <div className="text-xs text-gray-500">Total Bid Amount</div>
            <div className="text-lg font-semibold">
              {formatMoney(summary?.totalBidAmount || 0)}
            </div>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-4 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Driver-wise Bid Summary
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200">
              <thead>
                <tr className="bg-gray-50 text-left text-sm text-gray-600">
                  <th className="px-3 py-2 border">Driver</th>
                  <th className="px-3 py-2 border">Mobile</th>
                  <th className="px-3 py-2 border">Total</th>
                  <th className="px-3 py-2 border">Accepted</th>
                  <th className="px-3 py-2 border">Rejected</th>
                  <th className="px-3 py-2 border">Total Amount</th>
                  <th className="px-3 py-2 border">Latest Bid</th>
                  <th className="px-3 py-2 border">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedDriverSummary.map((item) => (
                  <tr key={item.driverId} className="text-sm">
                    <td className="px-3 py-2 border">
                      {item?.driver?.name || "N/A"}
                    </td>
                    <td className="px-3 py-2 border">
                      {item?.driver?.mobile || "N/A"}
                    </td>
                    <td className="px-3 py-2 border">{item.totalBids || 0}</td>
                    <td className="px-3 py-2 border">
                      {item.acceptedCount || 0}
                    </td>
                    <td className="px-3 py-2 border">
                      {item.rejectedCount || 0}
                    </td>
                    <td className="px-3 py-2 border">
                      {formatMoney(item.totalBidAmount)}
                    </td>
                    <td className="px-3 py-2 border">
                      {formatDateTime(item.latestBidAt)}
                    </td>
                    <td className="px-3 py-2 border">
                      <button
                        className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
                        onClick={() => goToDriverBidsPage(item)}
                      >
                        View Driver Bids
                      </button>
                    </td>
                  </tr>
                ))}
                {!driverSummary.length && (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      No driver summary found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {driverSummary.length > 0 && (
            <div className="flex justify-center items-center mt-4">
              <Pagination
                page={driverSummaryPage}
                totalPages={driverSummaryTotalPages}
                setPage={setDriverSummaryPage}
                limit={driverSummaryLimit}
                setLimit={setDriverSummaryLimit}
                prevPage={() =>
                  setDriverSummaryPage((prev) => (prev > 1 ? prev - 1 : prev))
                }
                nextPage={() =>
                  setDriverSummaryPage((prev) =>
                    prev < driverSummaryTotalPages ? prev + 1 : prev,
                  )
                }
              />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default withRouter(RideShareBids);
